/**
 * Learning Intelligence Signal Emission
 *
 * Frontend utility for emitting signals to the profile_signals table.
 * Used by Flashcards and other frontend features.
 *
 * Signals are processed by pg_cron or Edge Functions to update:
 * - profile_mastery_map (topic-level mastery)
 * - profile_beliefs (strengths/weaknesses)
 */

import { supabase } from './supabase';
import { normalizeSubject } from './subject-mapping';

/**
 * Product axis for Learning-Intelligence signals (mirrors
 * src/lib/ncbe-taxonomy.ts `Product` and services/process-signals
 * exam_blueprint.py PRODUCT_CONFIG keys). 'nextgen' = NCBE NextGen UBE
 * (mcq/iqs/pt); 'ube' = classic UBE/MBE product (mbe/mee/mpt).
 */
type SignalProduct = 'nextgen' | 'ube';

/**
 * The 3-axis Learning-Intelligence tags carried on a flashcard signal:
 * `product` (optional — omitted entirely for legacy/NextGen byte-compat),
 * `exam_format`, and `skill_domain`.
 *
 * Resolves the format/skill axis for a recall-type flashcard signal so all
 * builders stay consistent.
 *
 * Behavior contract:
 *   - issue_spotting cards → IQS axis ('iqs' / 'issue_spotting_analysis'),
 *     regardless of product (those test issue identification, not MBE doctrinal
 *     recall). This preserves existing behavior.
 *   - product='ube' doctrinal-recall cards → MBE axis: exam_format='mbe',
 *     product='ube', and NO skill_domain. Per the backend contract
 *     (exam_blueprint.py:40-43): "MBE is doctrinal-recall (no NCBE skill) —
 *     represented by the ABSENCE of a skill_domain on the signal, NOT a new
 *     slug." The doctrinal mastery lives on the SUBJECT axis (profile_mastery_map
 *     keyed by subject), which is why MBE carries no skill_domain.
 *   - everything else (no product / product='nextgen', non-IS) → the legacy
 *     default: exam_format='mcq', skill_domain='legal_research', NO product key.
 *     BYTE-IDENTICAL to pre-C17 behavior for all callers that don't opt in.
 */
function resolveFlashcardSignalAxes(opts: {
  cardType?: string;
  product?: SignalProduct;
}): { product?: SignalProduct; exam_format: string; skill_domain?: string } {
  // issue_spotting always maps to the IQS axis (product-independent).
  if (opts.cardType === 'issue_spotting') {
    return { exam_format: 'iqs', skill_domain: 'issue_spotting_analysis' };
  }
  // Classic-UBE MBE doctrinal recall: tag the product + MBE format, omit skill.
  if (opts.product === 'ube') {
    return { product: 'ube', exam_format: 'mbe' };
  }
  // Legacy / NextGen default — byte-identical to pre-C17.
  return { exam_format: 'mcq', skill_domain: 'legal_research' };
}

// Signal types and weights from the taxonomy
export const SIGNAL_WEIGHTS = {
  // Flashcards (moderate fidelity - recall not application)
  'flashcards.card_answered': 0.6,
  'flashcards.card_mastered': 0.7,
  'flashcards.card_pinned': 0.8,  // User-declared weakness - high signal
  'flashcards.card_lapsed': 0.8,  // FSRS lapse: forgotten card (scheduled_days >= 7 rated wrong)
  'flashcards.session_completed': 0.6,

  // Issue Spotter signals
  'issue_spotter.issue_pinned': 0.8,  // User-declared weakness - high signal

  // Chat (low fidelity - noisy)
  'chat.topic_discussed': 0.3,
  'chat.confusion_expressed': 0.4,
  'chat.follow_up_needed': 0.35,

  // Study Plan / Activity signals (high fidelity - explicit completion)
  'study_plan.task_completed': 0.75,
  'study_plan.diagnostic_completed': 0.85,  // High signal - direct assessment
  'study_plan.activity_skipped': 0.5,

  // Course signals (low fidelity - passive consumption, not active practice)
  'course.lesson_completed': 0.15,  // Exposure signal; completed_at enables knowledge decay

  // Syllabus signals (high fidelity - user explicitly uploaded course info)
  'syllabus.tasks_generated': 0.7,  // User has explicit course schedule

  // Onboarding signals (cold start - user profile declarations)
  'onboarding.status_declared': 0.4,
  'onboarding.goal_declared': 0.4,
  'onboarding.profile_declared': 0.4,

  // NextGen Question Bank (high fidelity — direct assessment with FSRS tracking)
  'nextgen.question_answered': 0.85,
  'nextgen.question_mastered': 0.9,   // First time question crosses mastery threshold
  'nextgen.question_lapsed': 0.9,     // FSRS lapse: previously stable question answered wrong
  'nextgen.miss_reason_reported': 0.8,  // Self-reported miss reason — high signal, explicit user input
  'nextgen.tier_transition_seen': 0,    // Frontend-only marker — user saw celebration toast; zero weight, not a learning event

  // Workbench (moderate fidelity — task completion with optional accuracy)
  'workbench.task_completed': 0.70,
  'workbench.task_graded': 0.85,

  // Adherence signals (§18 — consumed by adherence_engine, weight 0 for beliefs)
  'adherence.session_completed': 0.7,
  'adherence.session_abandoned': 0.6,
  'adherence.recommendation_dismissed': 0.5,
  'adherence.login_without_practice': 0.2,

  // Cognitive load signals (§18 — telemetry for mid-session adaptation)
  'adherence.response_time_increasing': 0,
  'adherence.answer_changes': 0,
  'adherence.error_rate_escalating': 0,
  'adherence.rapid_guessing': 0,
} as const;

export type SignalType = keyof typeof SIGNAL_WEIGHTS;

export interface ProfileSignal {
  profile_id: string; // This is user_id - will be looked up to get learning_profiles.id
  source_feature: string;
  source_session_id?: string;
  signal_type: SignalType;
  signal_data: Record<string, unknown>;
  signal_weight?: number;
}

/**
 * Emit a learning signal to the profile_signals table
 * Handles profile_id lookup automatically
 */
export async function emitSignal(signal: ProfileSignal): Promise<string | null> {
  try {
    // First get or create the learning profile
    const { data: profile } = await supabase
      .from('learning_profiles')
      .select('id')
      .eq('user_id', signal.profile_id)
      .maybeSingle();

    let profileId = profile?.id;

    if (!profileId) {
      // Create profile if doesn't exist
      // Use 'any' cast as learning_profiles may have additional required fields
      const { data: newProfile, error: createError } = await (supabase
        .from('learning_profiles') as unknown as { insert: (data: unknown) => { select: (cols: string) => { single: () => Promise<{ data: { id: string } | null; error: unknown }> } } })
        .insert({ user_id: signal.profile_id })
        .select('id')
        .single();

      if (createError) {
        console.error('[SIGNAL] Failed to create profile:', String(createError));
        return null;
      }
      profileId = newProfile?.id;
    }

    if (!profileId) {
      console.error('[SIGNAL] Could not get profile_id');
      return null;
    }

    // Insert signal - use type assertion to handle schema differences
    // Note: signal_weight is stored in signal_data for processing, not as a separate column
    const signalWeight = signal.signal_weight ?? SIGNAL_WEIGHTS[signal.signal_type] ?? 0.5;
    const signalRecord = {
      profile_id: profileId,
      source_feature: signal.source_feature,
      source_session_id: signal.source_session_id || null,
      signal_type: signal.signal_type,
      signal_data: { ...signal.signal_data, _weight: signalWeight },
      processed: false,
    };

    const { data, error } = await (supabase
      .from('profile_signals') as unknown as { insert: (data: unknown) => { select: (cols: string) => { single: () => Promise<{ data: { id: string } | null; error: unknown }> } } })
      .insert(signalRecord)
      .select('id')
      .single();

    if (error) {
      console.error('[SIGNAL] Insert failed:', String(error));
      return null;
    }

    console.log(`[SIGNAL] Emitted ${signal.signal_type}`);
    return data?.id ?? null;
  } catch (err) {
    console.error('[SIGNAL] Error emitting signal:', err);
    return null;
  }
}

/**
 * Emit multiple signals in a batch
 * Optimized: Single profile lookup + batch insert (2 queries instead of 2N)
 */
export async function emitSignalBatch(signals: ProfileSignal[]): Promise<number> {
  if (signals.length === 0) return 0;

  try {
    // All signals should be for the same user, so lookup profile once
    const userId = signals[0].profile_id;

    // Get or create the learning profile (single lookup)
    const { data: profile } = await supabase
      .from('learning_profiles')
      .select('id')
      .eq('user_id', userId)
      .maybeSingle();

    let profileId = profile?.id;

    if (!profileId) {
      // Create profile if doesn't exist
      const { data: newProfile, error: createError } = await (supabase
        .from('learning_profiles') as unknown as { insert: (data: unknown) => { select: (cols: string) => { single: () => Promise<{ data: { id: string } | null; error: unknown }> } } })
        .insert({ user_id: userId })
        .select('id')
        .single();

      if (createError) {
        console.error('[SIGNAL] Failed to create profile for batch:', String(createError));
        return 0;
      }
      profileId = newProfile?.id;
    }

    if (!profileId) {
      console.error('[SIGNAL] Could not get profile_id for batch');
      return 0;
    }

    // Build all signal records
    const signalRecords = signals.map(signal => ({
      profile_id: profileId,
      source_feature: signal.source_feature,
      source_session_id: signal.source_session_id || null,
      signal_type: signal.signal_type,
      signal_data: signal.signal_data,
      signal_weight: signal.signal_weight ?? SIGNAL_WEIGHTS[signal.signal_type] ?? 0.5,
      processed: false,
    }));

    // Batch insert all signals (single insert)
    const { data, error } = await (supabase
      .from('profile_signals') as unknown as { insert: (data: unknown[]) => { select: (cols: string) => Promise<{ data: { id: string }[] | null; error: unknown }> } })
      .insert(signalRecords)
      .select('id');

    if (error) {
      console.error('[SIGNAL] Batch insert failed:', String(error));
      return 0;
    }

    const count = data?.length ?? 0;
    console.log(`[SIGNAL] Batch emitted ${count}/${signals.length} signals`);
    return count;
  } catch (err) {
    console.error('[SIGNAL] Error in batch emit:', err);
    return 0;
  }
}

// ============================================================================
// FLASHCARD SIGNAL HELPERS
// ============================================================================

export function createFlashcardAnsweredSignal(
  profileId: string,
  cardData: {
    card_id: string;
    card_type?: string; // 'standard', 'multiple_choice', 'true_false', 'issue_spotting'
    subject?: string;
    topic?: string;
    is_correct: boolean;
    time_to_answer_ms?: number;
    // SRS enrichment fields (optional for backward compat)
    srs_rating?: string; // 'wrong', 'hard', 'got_it'
    stability_before?: number;
    stability_after?: number;
    difficulty_before?: number;
    difficulty_after?: number;
    interval_before_days?: number;
    interval_after_days?: number;
    elapsed_days?: number;
    retrievability_at_review?: number;
    review_count?: number;
    lapse_count?: number;
    is_lapse?: boolean;
    state_before?: number;
    state_after?: number;
    /**
     * Product the card belongs to. Omit (or 'nextgen') for existing
     * NextGen/legacy cards — output is byte-identical. Pass 'ube' for
     * classic-UBE MBE doctrinal-recall cards so the signal carries
     * product='ube' + exam_format='mbe' + no skill_domain.
     */
    product?: SignalProduct;
  }
): ProfileSignal {
  const { product, ...rest } = cardData;
  const axes = resolveFlashcardSignalAxes({ cardType: cardData.card_type, product });
  return {
    profile_id: profileId,
    source_feature: 'flashcards',
    signal_type: 'flashcards.card_answered',
    signal_data: {
      ...rest,
      // Normalize subject to canonical slug for Intelligence system
      subject: cardData.subject ? normalizeSubject(cardData.subject) ?? cardData.subject : undefined,
      // 3-axis tags: product (optional), exam_format, skill_domain (optional)
      ...axes,
    },
  };
}

export function createFlashcardMasteredSignal(
  profileId: string,
  cardData: {
    card_id: string;
    card_type?: string; // 'standard', 'multiple_choice', 'true_false', 'issue_spotting'
    subject?: string;
    topic?: string;
    consecutive_correct?: number;
    total_attempts?: number;
    // SRS enrichment fields (auto-emitted when interval crosses 21d)
    interval_days?: number;
    stability?: number;
    review_count?: number;
    lapse_count?: number;
    /** See createFlashcardAnsweredSignal — pass 'ube' for MBE cards. */
    product?: SignalProduct;
  }
): ProfileSignal {
  const { product, ...rest } = cardData;
  const axes = resolveFlashcardSignalAxes({ cardType: cardData.card_type, product });
  return {
    profile_id: profileId,
    source_feature: 'flashcards',
    signal_type: 'flashcards.card_mastered',
    signal_data: {
      ...rest,
      // Normalize subject to canonical slug for Intelligence system
      subject: cardData.subject ? normalizeSubject(cardData.subject) ?? cardData.subject : undefined,
      ...axes,
    },
  };
}

export function createFlashcardSessionSignal(
  profileId: string,
  sessionData: {
    session_id?: string;
    cards_reviewed: number;
    correct_count: number;
    accuracy: number;
    subjects_covered: string[];
    time_spent_seconds?: number;
    // SRS enrichment fields
    wrong_count?: number;
    hard_count?: number;
    got_it_count?: number;
    cards_lapsed?: number;
    new_cards_introduced?: number;
    avg_retrievability_before?: number;
    avg_retrievability_after?: number;
    /**
     * Product the session belongs to. Omit (or 'nextgen') for existing
     * NextGen/legacy sessions — byte-identical. Pass 'ube' for an
     * all-MBE study session so the session signal carries product='ube' +
     * exam_format='mbe' (no skill_domain).
     */
    product?: SignalProduct;
  }
): ProfileSignal {
  const { product, ...rest } = sessionData;
  // Session-level: no per-card card_type, so the IS branch never applies here.
  const axes = resolveFlashcardSignalAxes({ product });
  return {
    profile_id: profileId,
    source_feature: 'flashcards',
    source_session_id: sessionData.session_id,
    signal_type: 'flashcards.session_completed',
    signal_data: {
      ...rest,
      // Normalize all subjects to canonical slugs for Intelligence system
      subjects_covered: sessionData.subjects_covered.map(s => normalizeSubject(s) ?? s),
      ...axes,
    },
  };
}

/**
 * Create a signal when user pins a flashcard for focus.
 * Pin indicates a user-declared area of weakness/challenge.
 * This is a high-weight signal (0.8) because it's explicit user input.
 *
 * Pin is additive to mastery - a card can be both Pinned AND Mastered.
 * The signal feeds into JDS Intelligence to generate personalized
 * practice questions targeting the user's weak areas.
 */
export function createFlashcardPinnedSignal(
  profileId: string,
  cardData: {
    card_id: string;
    card_type?: string; // 'standard', 'multiple_choice', 'true_false', 'issue_spotting'
    subject?: string;
    topic?: string;
    is_pinned: boolean;  // true = pinned, false = unpinned
    /** See createFlashcardAnsweredSignal — pass 'ube' for MBE cards. */
    product?: SignalProduct;
  }
): ProfileSignal {
  const { product, ...rest } = cardData;
  const axes = resolveFlashcardSignalAxes({ cardType: cardData.card_type, product });
  return {
    profile_id: profileId,
    source_feature: 'flashcards',
    signal_type: 'flashcards.card_pinned',
    signal_data: {
      ...rest,
      // Normalize subject to canonical slug for Intelligence system
      subject: cardData.subject ? normalizeSubject(cardData.subject) ?? cardData.subject : undefined,
      ...axes,
    },
  };
}

/**
 * Create a signal when a previously learned card is forgotten (lapse).
 * A lapse = rating Wrong on a card with scheduled_days >= 7.
 * High-weight signal (0.8) — forgetting something you knew is strong
 * evidence of a knowledge gap.
 */
export function createFlashcardLapsedSignal(
  profileId: string,
  cardData: {
    card_id: string;
    card_type?: string; // 'standard', 'multiple_choice', 'true_false', 'issue_spotting'
    subject?: string;
    topic?: string;
    previous_interval_days: number;
    previous_stability: number;
    lapse_count: number;
    days_overdue?: number;
    retrievability_at_review?: number;
    /** See createFlashcardAnsweredSignal — pass 'ube' for MBE cards. */
    product?: SignalProduct;
  }
): ProfileSignal {
  const { product, ...rest } = cardData;
  const axes = resolveFlashcardSignalAxes({ cardType: cardData.card_type, product });
  return {
    profile_id: profileId,
    source_feature: 'flashcards',
    signal_type: 'flashcards.card_lapsed',
    signal_data: {
      ...rest,
      subject: cardData.subject ? normalizeSubject(cardData.subject) ?? cardData.subject : undefined,
      // 3-axis tags: product (optional), exam_format, skill_domain (optional)
      ...axes,
    },
  };
}

/**
 * Create a signal when user pins an Issue Spotter issue for focus.
 * Pin indicates a user-declared area of weakness/challenge.
 * This is a high-weight signal (0.8) because it's explicit user input.
 *
 * Pin is additive - an issue can be both Spotted AND Pinned.
 * The signal feeds into JDS Intelligence to generate personalized
 * practice scenarios targeting the user's weak areas.
 */
export function createIssuePinnedSignal(
  profileId: string,
  issueData: {
    issue_id: string;
    subject_area: string;
    issue_category: string;
    issue_statement: string;
    is_pinned: boolean;  // true = pinned, false = unpinned
    session_id?: string;
  }
): ProfileSignal {
  return {
    profile_id: profileId,
    source_feature: 'issue_spotter',
    source_session_id: issueData.session_id,
    signal_type: 'issue_spotter.issue_pinned',
    signal_data: {
      ...issueData,
      // Normalize subject_area to canonical slug for Intelligence system
      subject_area: normalizeSubject(issueData.subject_area) ?? issueData.subject_area,
      exam_format: 'iqs',  // 3-axis: issue spotter → IQS format
      skill_domain: 'issue_spotting_analysis',
    },
  };
}

// ============================================================================
// COURSE LESSON SIGNAL HELPERS
// ============================================================================

/**
 * Create a signal when a user completes a course lesson.
 * Low-weight signal (0.15) — lesson completion is passive exposure,
 * not active practice. The completed_at timestamp in lesson_progress
 * enables knowledge decay weighting.
 *
 * Applies to both JDS standard courses and NextGen courses.
 */
export function createLessonCompletedSignal(
  profileId: string,
  lessonData: {
    lesson_id: string;
    course_id: string;
    course_type: string;        // 'nextgen' | 'standard'
    subject?: string;            // canonical slug from course_subjects resolution
    lesson_title?: string;
    module_id?: string;
  }
): ProfileSignal {
  return {
    profile_id: profileId,
    source_feature: 'course',
    signal_type: 'course.lesson_completed',
    signal_data: {
      ...lessonData,
      subject: lessonData.subject ? normalizeSubject(lessonData.subject) ?? lessonData.subject : undefined,
    },
  };
}

/**
 * Self-reported miss reason values for MCQ questions.
 * Used in the "Why did you miss this?" UI after incorrect answers.
 */
export type MissReason =
  | 'didnt_know_rule'
  | 'knew_rule_applied_wrong'
  | 'misread_facts'
  | 'confused_with_similar_rule'
  | 'guessed';

/**
 * Create a signal when a user self-reports why they missed an MCQ question.
 * High-weight signal (0.8) — explicit user input about root cause of error.
 * Emitted separately from the question_answered signal (Option A pattern).
 */
export function createMissReasonSignal(
  profileId: string,
  data: {
    question_id: string;
    self_reported_reason: MissReason;
    subject?: string;
    practice_mode: string;
    /**
     * Exam format the question was practiced under. Defaults to 'mcq' for
     * backward compatibility with MCQ practice callers that don't thread it
     * through yet. IQS and PT practice pages MUST pass this explicitly so
     * the signal attributes correctly to their format's accuracy pipeline.
     */
    exam_format?: 'mcq' | 'mcq_b' | 'iqs' | 'pt' | 'lrpt';
    session_context?: { session_mode: 'practice' | 'simulation' };
  }
): ProfileSignal {
  return {
    profile_id: profileId,
    source_feature: 'nextgen',
    signal_type: 'nextgen.miss_reason_reported',
    signal_data: {
      question_id: data.question_id,
      self_reported_reason: data.self_reported_reason,
      subject: data.subject ? normalizeSubject(data.subject) ?? data.subject : null,
      exam_format: data.exam_format ?? 'mcq',
      practice_mode: data.practice_mode,
      session_context: data.session_context ?? null,
    },
  };
}

export function createNextGenMasteredSignal(
  profileId: string,
  data: {
    question_id: string;
    subject?: string;
    skill_domain?: string;
    exam_format?: string;
    difficulty?: number;
    /** Primary NCBE task codes (e.g. ['T6']) — used by signal_processor.py for subcategory resolution */
    ncbe_tasks?: string[];
  }
): ProfileSignal {
  return {
    profile_id: profileId,
    source_feature: 'nextgen',
    signal_type: 'nextgen.question_mastered',
    signal_data: {
      question_id: data.question_id,
      subject: data.subject ? normalizeSubject(data.subject) ?? data.subject : null,
      skill_domain: data.skill_domain ?? null,
      exam_format: data.exam_format ?? null,
      difficulty: data.difficulty ?? null,
      ncbe_tasks: data.ncbe_tasks ?? null,
    },
  };
}

export function createNextGenLapsedSignal(
  profileId: string,
  data: {
    question_id: string;
    subject?: string;
    skill_domain?: string;
    exam_format?: string;
    difficulty?: number;
    lapse_count?: number;
    /** Primary NCBE task codes (e.g. ['T6']) — used by signal_processor.py for subcategory resolution */
    ncbe_tasks?: string[];
  }
): ProfileSignal {
  return {
    profile_id: profileId,
    source_feature: 'nextgen',
    signal_type: 'nextgen.question_lapsed',
    signal_data: {
      question_id: data.question_id,
      subject: data.subject ? normalizeSubject(data.subject) ?? data.subject : null,
      skill_domain: data.skill_domain ?? null,
      exam_format: data.exam_format ?? null,
      difficulty: data.difficulty ?? null,
      lapse_count: data.lapse_count ?? null,
      ncbe_tasks: data.ncbe_tasks ?? null,
    },
  };
}

/**
 * Create a signal when the user sees the Overall Readiness tier transition
 * toast for a given tier. Frontend-only marker — zero weight, not a learning
 * event. Used to suppress re-showing the celebration across sessions/devices.
 *
 * Stored in profile_signals so suppression is cross-device (localStorage was
 * intentionally rejected for this use case).
 *
 * Tiers are one-way: we never delete a seen signal. If a user's tier drops
 * (e.g. Strong → Building because of new wrong answers) and then climbs back,
 * the toast is suppressed because the original seen signal still exists. This
 * prevents oscillation at tier boundaries.
 */
export function createTierTransitionSeenSignal(
  profileId: string,
  data: {
    tier: 'building' | 'strong' | 'mastered';
    readiness_score: number;
  }
): ProfileSignal {
  return {
    profile_id: profileId,
    source_feature: 'nextgen',
    signal_type: 'nextgen.tier_transition_seen',
    signal_data: {
      tier: data.tier,
      readiness_score: data.readiness_score,
      seen_at: new Date().toISOString(),
    },
  };
}

// ============================================================================
// WORKBENCH SIGNAL HELPERS
// ============================================================================

/**
 * Create a signal when a NextGen workbench task is completed.
 * Moderate-weight signal (0.70) — active practice with timing and engagement data.
 * Feeds subject, skill_domain, and exam_format into the 3-axis belief model.
 */
export function createWorkbenchTaskCompletedSignal(params: {
  profileId: string;
  taskId: string;
  taskType: string;
  examFormat: string;
  skillDomain: string;
  subjects: string[];
  mode: string;
  timeSpentSeconds: number;
  sourcesViewed: number;
  highlightsCount: number;
  itemsCompleted?: number;
  itemsCorrect?: number;
}): ProfileSignal {
  return {
    profile_id: params.profileId,
    source_feature: 'workbench',
    signal_type: 'workbench.task_completed',
    signal_data: {
      task_id: params.taskId,
      task_type: params.taskType,
      exam_format: params.examFormat,
      skill_domain: params.skillDomain,
      subjects: params.subjects.map(s => normalizeSubject(s) ?? s),
      mode: params.mode,
      time_spent_seconds: params.timeSpentSeconds,
      sources_viewed: params.sourcesViewed,
      highlights_count: params.highlightsCount,
      items_completed: params.itemsCompleted ?? null,
      items_correct: params.itemsCorrect ?? null,
    },
  };
}

/**
 * Create a signal when a NextGen workbench task is graded by AI.
 * High-weight signal (0.85) — structured rubric scoring with per-skill breakdown.
 * Feeds subject, skill_domain, and exam_format into the 3-axis belief model.
 */
export function createWorkbenchTaskGradedSignal(params: {
  profileId: string;
  taskId: string;
  responseId: string;
  taskType: string;
  examFormat: string;
  skillDomain: string;
  subjects: string[];
  overallScore: number;
  maxScore: number;
  rubricScores: Array<{ criterion: string; skill: string; score: number; max: number }>;
  gradingModel: string;
}): ProfileSignal {
  return {
    profile_id: params.profileId,
    source_feature: 'workbench',
    signal_type: 'workbench.task_graded',
    signal_data: {
      task_id: params.taskId,
      response_id: params.responseId,
      task_type: params.taskType,
      exam_format: params.examFormat,
      skill_domain: params.skillDomain,
      subjects: params.subjects.map(s => normalizeSubject(s) ?? s),
      overall_score: params.overallScore,
      max_score: params.maxScore,
      rubric_scores: params.rubricScores,
      grading_model: params.gradingModel,
    },
  };
}

// ============================================================================
// STUDY PLAN / ACTIVITY SIGNAL HELPERS
// ============================================================================

/**
 * Create a signal when a study plan task is completed.
 * This is a high-weight signal (0.75) indicating explicit learning progress.
 */
export function createTaskCompletedSignal(
  profileId: string,
  taskData: {
    task_id: string;
    task_type: string;
    subject?: string;
    topic?: string;
    estimated_minutes?: number;
    actual_minutes?: number;
    study_plan_id?: string;
    completion_notes?: string;
  }
): ProfileSignal {
  return {
    profile_id: profileId,
    source_feature: 'study_plan',
    signal_type: 'study_plan.task_completed',
    signal_data: {
      ...taskData,
      // Normalize subject to canonical slug for Intelligence system
      subject: taskData.subject ? normalizeSubject(taskData.subject) ?? taskData.subject : undefined,
    },
  };
}

/**
 * Create a signal when a diagnostic assessment is completed.
 * This is the highest-weight study signal (0.85) - direct skill assessment.
 */
export function createDiagnosticCompletedSignal(
  profileId: string,
  diagnosticData: {
    task_id: string;
    subject: string;
    score_percentage?: number;
    questions_answered?: number;
    time_spent_seconds?: number;
    study_plan_id?: string;
  }
): ProfileSignal {
  return {
    profile_id: profileId,
    source_feature: 'study_plan',
    signal_type: 'study_plan.diagnostic_completed',
    signal_data: {
      ...diagnosticData,
      // Normalize subject to canonical slug for Intelligence system
      subject: normalizeSubject(diagnosticData.subject) ?? diagnosticData.subject,
    },
  };
}

/**
 * Create a signal when a task is skipped/dismissed.
 * Lower weight (0.5) but still useful for understanding user preferences.
 */
export function createActivitySkippedSignal(
  profileId: string,
  taskData: {
    task_id: string;
    task_type: string;
    subject?: string;
    topic?: string;
    reason?: string;
  }
): ProfileSignal {
  return {
    profile_id: profileId,
    source_feature: 'study_plan',
    signal_type: 'study_plan.activity_skipped',
    signal_data: {
      ...taskData,
      // Normalize subject to canonical slug for Intelligence system
      subject: taskData.subject ? normalizeSubject(taskData.subject) ?? taskData.subject : undefined,
    },
  };
}

// ============================================================================
// SYLLABUS SIGNAL HELPERS
// ============================================================================

/**
 * Create a signal when tasks are generated from a syllabus.
 * This informs Intelligence about the user's course schedule and subjects.
 */
export function createSyllabusTasksGeneratedSignal(
  profileId: string,
  syllabusData: {
    syllabus_id: string;
    course_name: string;
    exam_tasks_created: number;
    assignment_tasks_created: number;
    subjects_covered: string[];
    upcoming_exam_dates?: string[];
  }
): ProfileSignal {
  return {
    profile_id: profileId,
    source_feature: 'syllabus',
    signal_type: 'syllabus.tasks_generated',
    signal_data: {
      ...syllabusData,
      // Normalize all subjects to canonical slugs for Intelligence system
      subjects_covered: syllabusData.subjects_covered.map(s => normalizeSubject(s) ?? s),
    },
  };
}

// ============================================================================
// ONBOARDING SIGNAL HELPERS
// ============================================================================

/**
 * Create a signal when user declares their status during onboarding.
 * Cold start signal (0.4 weight) — seeds the learning profile.
 */
export function createStatusDeclaredSignal(
  profileId: string,
  status: string
): ProfileSignal {
  return {
    profile_id: profileId,
    source_feature: 'onboarding',
    signal_type: 'onboarding.status_declared',
    signal_data: {
      current_status: status,
    },
  };
}

/**
 * Create a signal when user declares their goal during onboarding.
 * Cold start signal (0.4 weight) — seeds the learning profile.
 */
export function createGoalDeclaredSignal(
  profileId: string,
  goal: string,
  status: string
): ProfileSignal {
  return {
    profile_id: profileId,
    source_feature: 'onboarding',
    signal_type: 'onboarding.goal_declared',
    signal_data: {
      goal_mode: goal,
      current_status: status,
    },
  };
}

/**
 * Create a signal when onboarding is completed (all profile data declared).
 * Cold start signal (0.4 weight) — full profile snapshot for Intelligence bootstrapping.
 */
export function createProfileDeclaredSignal(
  profileId: string,
  profileData: {
    current_status: string;
    goal_mode: string;
    courses: string[];
    // Declared bar jurisdiction (journey context for Study Plan). Optional —
    // only law-school / bar-prep users select a target_bar_state during onboarding.
    target_bar_state?: string | null;
  }
): ProfileSignal {
  return {
    profile_id: profileId,
    source_feature: 'onboarding',
    signal_type: 'onboarding.profile_declared',
    signal_data: {
      ...profileData,
      courses: profileData.courses.map(s => normalizeSubject(s) ?? s),
    },
  };
}
